import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin, Package, User, Sparkles } from "lucide-react";
import api from "../lib/api";
import { VOLUMES } from "../lib/data";
import AddressAutocomplete from "../components/AddressAutocomplete";

const STEPS = [
  { id: 1, label: "Trajet", icon: MapPin },
  { id: 2, label: "Volume & date", icon: Package },
  { id: 3, label: "Vos coordonnées", icon: User },
];

export default function QuoteForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetCompanySlug = searchParams.get("company");
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    departure_address: searchParams.get("dep") || "",
    departure_city: "",
    arrival_address: searchParams.get("arr") || "",
    arrival_city: "",
    volume: VOLUMES[1],
    moving_date: searchParams.get("date") || "",
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    message: "",
    consent: false,
  });

  useEffect(() => {
    document.title = "Demande de devis — Déménageur Pro";
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return form.departure_address && form.arrival_address;
    if (step === 2) return form.volume && form.moving_date;
    if (step === 3) return form.first_name && form.last_name && form.email && form.phone && form.consent;
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canNext()) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/leads", {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        email: form.email,
        departure_address: form.departure_address,
        departure_city: form.departure_city,
        arrival_address: form.arrival_address,
        arrival_city: form.arrival_city,
        volume: form.volume,
        moving_date: form.moving_date,
        message: form.message,
        target_company_slug: targetCompanySlug || null,
        source_site: "demenageur-pro",
        // Section 12 — traçabilité du consentement
        consent: form.consent === true,
        marketing_consent: form.marketing_consent === true,
        consent_version: "2026-02-15-v1",
        consent_source_page: typeof window !== "undefined" ? window.location.pathname : "",
        consent_max_partners: 5,
        consent_timestamp: new Date().toISOString(),
      });
      // Google Ads conversion tracking
      if (typeof window.gtag_report_conversion === "function") {
        try { window.gtag_report_conversion(); } catch (_) { /* no-op */ }
      }
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-6 py-20" data-testid="quote-success">
        <div className="max-w-2xl mx-auto text-center bg-white rounded-3xl border border-[#E5E7EB] p-12 shadow-sm">
          <div className="w-20 h-20 bg-[#E8FBF3] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#2BD391]" />
          </div>
          <div className="eyebrow mb-3">Demande envoyée</div>
          <h1 className="font-heading font-extrabold text-4xl mb-4 text-[#2C3E50]">
            Merci {form.first_name} !
          </h1>
          <p className="text-[#6B7280] mb-8">
            Votre demande a été transmise à nos entreprises de déménagement partenaires. Elles vous contacteront directement par téléphone ou email pour vous proposer un devis.
          </p>
          <button onClick={() => navigate("/")} className="btn-primary" data-testid="back-home-btn">
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up bg-[#F7F9FB] py-12 md:py-20 min-h-screen">
      <div className="container mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8FBF3] text-[#1AA66C] text-xs font-semibold mb-5">
              <Sparkles className="w-3.5 h-3.5" /> 100% gratuit · sans engagement
            </div>
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl mb-3 text-[#2C3E50]">
              Recevez plusieurs devis<br/>en quelques clics
            </h1>
            <p className="text-[#6B7280]">Remplissez ce formulaire en 2 minutes — vos coordonnées seront transmises à nos entreprises partenaires.</p>
          </div>

          {/* Steps */}
          <div className="flex items-center justify-center gap-2 md:gap-3 mb-10" data-testid="step-indicator">
            {STEPS.map((s, i) => {
              const active = s.id === step;
              const done = s.id < step;
              return (
                <div key={s.id} className="flex items-center gap-2 md:gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${active ? "bg-[#2BD391] text-white shadow-[0_4px_14px_rgba(43,211,145,0.35)]" : done ? "bg-[#1AA66C] text-white" : "bg-white border-2 border-[#E5E7EB] text-[#6B7280]"}`}>
                    {done ? <CheckCircle2 className="w-5 h-5" /> : s.id}
                  </div>
                  <span className={`text-sm font-semibold hidden sm:inline ${active ? "text-[#2C3E50]" : "text-[#6B7280]"}`}>{s.label}</span>
                  {i < STEPS.length - 1 && <div className="w-6 md:w-10 h-px bg-[#E5E7EB]" />}
                </div>
              );
            })}
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm p-6 md:p-10" data-testid="quote-form">
            {step === 1 && (
              <div className="space-y-5 animate-slide-right">
                <h2 className="font-heading font-extrabold text-2xl mb-4 text-[#2C3E50]">Votre trajet</h2>
                <Field label="Adresse de départ" testid="field-departure-address">
                  <AddressAutocomplete
                    value={form.departure_address}
                    onChange={(v) => set("departure_address", v)}
                    onSelect={(_p, details) => {
                      if (details?.formatted_address) set("departure_address", details.formatted_address);
                      if (details?.city) set("departure_city", details.city);
                    }}
                    placeholder="ex: 12 rue de la République, Paris"
                    className="input-square !pl-11"
                    iconClassName="left-3.5"
                    testid="input-departure-address"
                  />
                </Field>
                <Field label="Adresse d'arrivée" testid="field-arrival-address">
                  <AddressAutocomplete
                    value={form.arrival_address}
                    onChange={(v) => set("arrival_address", v)}
                    onSelect={(_p, details) => {
                      if (details?.formatted_address) set("arrival_address", details.formatted_address);
                      if (details?.city) set("arrival_city", details.city);
                    }}
                    placeholder="ex: 45 boulevard des Belges, Lyon"
                    className="input-square !pl-11"
                    iconClassName="left-3.5"
                    testid="input-arrival-address"
                  />
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5 animate-slide-right">
                <h2 className="font-heading font-extrabold text-2xl mb-4 text-[#2C3E50]">Volume & date</h2>
                <Field label="Volume estimé">
                  <select className="input-square" value={form.volume} onChange={(e) => set("volume", e.target.value)} data-testid="input-volume">
                    {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Field>
                <Field label="Date du déménagement">
                  <input type="date" className="input-square" value={form.moving_date} onChange={(e) => set("moving_date", e.target.value)} data-testid="input-date" />
                </Field>
                <Field label="Message complémentaire (optionnel)">
                  <textarea rows={4} className="input-square !rounded-2xl" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Étage, ascenseur, objets fragiles, etc." data-testid="input-message" />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5 animate-slide-right">
                <h2 className="font-heading font-extrabold text-2xl mb-4 text-[#2C3E50]">Vos coordonnées</h2>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Prénom">
                    <input className="input-square" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="input-first-name" />
                  </Field>
                  <Field label="Nom">
                    <input className="input-square" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="input-last-name" />
                  </Field>
                </div>
                <Field label="Email">
                  <input type="email" className="input-square" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
                </Field>
                <Field label="Téléphone">
                  <input type="tel" className="input-square" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="06 12 34 56 78" data-testid="input-phone" />
                </Field>
                <label className="flex items-start gap-3 text-sm cursor-pointer p-4 rounded-xl bg-[#F7F9FB] hover:bg-[#E8FBF3] transition-colors">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-1 accent-[#2BD391] w-4 h-4"
                    data-testid="input-consent"
                    required
                  />
                  <span className="text-[#6B7280]">
                    J'accepte que les informations de ma demande soient utilisées par Déménageur Pro afin de me mettre en relation avec un <strong>maximum de 5 entreprises</strong> de déménagement partenaires correspondant à mon projet et à ma zone géographique. Ces entreprises pourront me contacter par téléphone, SMS ou email afin de me proposer un devis. J'ai pris connaissance de la <a href="/confidentialite" target="_blank" rel="noopener" className="text-[#1AA66C] font-semibold hover:underline" data-testid="link-privacy">Politique de confidentialité</a> et de la <a href="/partenaires" target="_blank" rel="noopener" className="text-[#1AA66C] font-semibold hover:underline" data-testid="link-partners-list">liste des partenaires</a>. <span className="text-[#2C3E50] font-semibold">*</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 text-sm cursor-pointer p-4 rounded-xl border border-dashed border-[#E5E7EB] hover:bg-[#F7F9FB] transition-colors">
                  <input
                    type="checkbox"
                    checked={form.marketing_consent || false}
                    onChange={(e) => set("marketing_consent", e.target.checked)}
                    className="mt-1 accent-[#2BD391] w-4 h-4"
                    data-testid="input-marketing-consent"
                  />
                  <span className="text-[#6B7280]">
                    J'accepte de recevoir ultérieurement des offres et communications commerciales de Déménageur Pro par email ou SMS. <span className="text-[#9AA5B1] text-xs">(facultatif)</span>
                  </span>
                </label>
              </div>
            )}

            {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 text-sm text-red-700 rounded-xl" data-testid="form-error">{error}</div>}

            <div className="mt-8 flex items-center justify-between gap-3">
              {step > 1 ? (
                <button type="button" onClick={() => setStep(step - 1)} className="btn-outline" data-testid="prev-step-btn">
                  <ArrowLeft className="w-4 h-4" /> Précédent
                </button>
              ) : <span />}

              {step < 3 ? (
                <button type="button" onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} className="btn-primary disabled:opacity-50 disabled:pointer-events-none" data-testid="next-step-btn">
                  Suivant <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="submit" disabled={!canNext() || submitting} className="btn-primary btn-large disabled:opacity-50 disabled:pointer-events-none" data-testid="submit-quote-btn">
                  {submitting ? "Envoi…" : "Envoyer ma demande"}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">{label}</label>
    {children}
  </div>
);
